/**
 * @module lib/routeAccess
 *
 * Which routes only internal (admin) users may open.
 *
 * Kept out of `App.svelte` so the guard is a function that can be asked
 * questions in a test — in particular the one that matters for the OAuth
 * handshake: `/oauth/consent` must *not* be admin-only, because every internal
 * role is allowed to authorize an MCP client for itself, and a customer bounced
 * off that route would land on the home page with a dangling authorization
 * request and no explanation.
 */

/**
 * Route prefixes a customer account may not open.
 * @type {string[]}
 */
export const ADMIN_ONLY_ROUTES = [
  '/customers',
  // Managing who resells for us, and whether their certification stands, is
  // not a decision a reseller may take. Note the plural: the reseller's own
  // page is '/my-partner', which this prefix cannot swallow.
  '/partners',
  '/products',
  '/reports',
  '/timesheets',
  '/settings',
  '/admin/tokens',
  // Qualified signing. Admin-only on the server too (`requireAdmin()` on the
  // whole /api/signatures router); this only keeps a customer from opening a
  // page that would answer 403 to everything on it.
  '/firma',
];

/**
 * Admin-area routes a reseller account may nevertheless open.
 *
 * A partner is not an internal user, but it is not a customer either: it lists
 * its own customers and reads their compliance reports. Both pages are scoped
 * on the server (`middleware/scope.js`), so what is relaxed here is only the
 * SPA's own bouncer, never the data.
 *
 * @type {string[]}
 */
export const PARTNER_ROUTES = [
  '/customers',
  '/compliance-reports',
  '/my-partner',
];

/**
 * Whether a reseller account should be bounced off a path.
 *
 * @param {string} path Current route path, without the leading '#'
 * @returns {boolean}
 */
export function isBlockedForPartner(path) {
  if (!path) return false;
  if (PARTNER_ROUTES.some((route) => path.startsWith(route))) return false;
  return isAdminOnlyRoute(path);
}

/**
 * Whether a path belongs to an admin-only area.
 *
 * Matched by prefix, so `/customers/42` is covered by `/customers`.
 *
 * @param {string} path Current route path, without the leading '#'
 * @returns {boolean}
 */
export function isAdminOnlyRoute(path) {
  if (!path) return false;
  return ADMIN_ONLY_ROUTES.some((route) => path.startsWith(route));
}

/**
 * Routes open without a session. The password-recovery pages are reachable
 * signed in as well: a held customer opens the mailed link in the same browser.
 * `/logout` (the INWD console's "also sign out of WEBDEPLOY") answers either way.
 * @type {string[]}
 */
export const PUBLIC_ROUTES = ['/login', '/forgot-password', '/reset-password', '/logout'];

/** Where a signed-in customer with an unverified address is held. */
export const ADDRESS_CONFIRMATION_ROUTE = '/verify-email-pending';

/**
 * Pages a held customer may still open: the hold itself and the two password
 * pages that end it.
 * @type {string[]}
 */
const ADDRESS_CONFIRMATION_ROUTES = [ADDRESS_CONFIRMATION_ROUTE, '/forgot-password', '/reset-password', '/logout'];

/**
 * Whether a path is open without a session.
 *
 * @param {string} path Current route path, without the leading '#'
 * @returns {boolean}
 */
export function isPublicRoute(path) {
  return PUBLIC_ROUTES.includes(path);
}

/**
 * Whether a signed-in account must be held on the address-confirmation page.
 * Only a customer whose login address the server reports as not verified
 * (`emailVerified === false`); staff, and an unknown state, are never held:
 * the portal API refuses an unverified customer on its own.
 *
 * @param {{isCustomer: boolean, emailVerified: boolean|null|undefined, path: string}} input
 * @returns {boolean}
 */
export function mustConfirmAddress({ isCustomer, emailVerified, path }) {
  return isCustomer === true && emailVerified === false && !ADDRESS_CONFIRMATION_ROUTES.includes(path);
}
